/**
 * Turning keys and message descriptors into sentences.
 *
 * Everything here is a pure function of (locale, key, params): no state, no
 * clock, no network. The same descriptor always renders the same sentence, so
 * localisation can never influence game logic.
 */

import { TILE_INFO } from "@kikik/mahjong-core";
import type { GroupKind, PlayerId, SeatWind, TileKind } from "@kikik/mahjong-core";
import { dictionary } from "./dictionary.js";
import type { LessonCopy, LessonId, GlossaryEntry } from "./content.js";
import { LESSON_IDS } from "./content.js";
import type { Locale } from "./locale.js";
import type { ClaimLabelKind, Message, MessageParam } from "./message.js";
import type { StringKey } from "./strings.id.js";

export interface FormatOptions {
  /** How to name a seat; modes differ (bots in Solo, people in Online). */
  playerName?: (playerId: PlayerId) => string;
}

/** A key with no translation renders visibly wrong on purpose, never silently. */
function missing(key: string): string {
  return `[[${key}]]`;
}

export function hasString(locale: Locale, key: string): boolean {
  return Object.prototype.hasOwnProperty.call(dictionary(locale).strings, key);
}

/** Raw template for a key, before interpolation. */
export function template(locale: Locale, key: string): string {
  const value = dictionary(locale).strings[key as StringKey];
  return typeof value === "string" ? value : missing(key);
}

function interpolate(
  text: string,
  params: Record<string, MessageParam> | undefined,
  locale: Locale,
  options: FormatOptions,
): string {
  if (!params) return text;
  return text.replace(/\{(\w+)\}/g, (whole, name: string) => {
    const value = params[name];
    return value === undefined ? whole : renderParam(value, locale, options);
  });
}

function renderParam(param: MessageParam, locale: Locale, options: FormatOptions): string {
  if (typeof param === "string") return param;
  if (typeof param === "number") return String(param);
  switch (param.$) {
    case "tile":
      return tileName(locale, param.kind);
    case "tiles":
      return tileList(locale, param.kinds);
    case "meld":
      return meldName(locale, param.kind);
    case "claim":
      return claimLabel(locale, param.kind);
    case "player":
      return options.playerName?.(param.id) ?? defaultPlayerName(locale, param.id);
    case "msg":
      return formatMessage(
        locale,
        param.params ? { key: param.key, params: param.params } : { key: param.key },
        options,
      );
    default:
      return "";
  }
}

/** Look up one key and fill in its placeholders. */
export function translate(
  locale: Locale,
  key: string,
  params?: Record<string, MessageParam>,
  options: FormatOptions = {},
): string {
  return interpolate(template(locale, key), params, locale, options);
}

/** Render a language-neutral message descriptor from the coach or the log. */
export function formatMessage(
  locale: Locale,
  message: Message,
  options: FormatOptions = {},
): string {
  return translate(locale, message.key, message.params, options);
}

// ---------- tiles ----------

/** "5 Bambu" / "5 Bamboo", "Naga Merah" / "Red Dragon". */
export function tileName(locale: Locale, kind: TileKind): string {
  const info = TILE_INFO[kind];
  if (info.isHonor) return template(locale, `tile.${kind}`);
  return translate(locale, "tile.suited", {
    rank: info.rank,
    suit: suitName(locale, kind),
  });
}

export function suitName(locale: Locale, kind: TileKind): string {
  return template(locale, `suit.${TILE_INFO[kind].suit}`);
}

/** "4 Bambu, 5 Bambu dan 6 Bambu" / "4 Bamboo, 5 Bamboo and 6 Bamboo". */
export function tileList(locale: Locale, kinds: readonly TileKind[]): string {
  const names = kinds.map((kind) => tileName(locale, kind));
  if (names.length <= 1) return names[0] ?? "";
  const separator = template(locale, "tile.listSeparator");
  const and = template(locale, "tile.listAnd");
  return `${names.slice(0, -1).join(separator)}${and}${names[names.length - 1]}`;
}

// ---------- table vocabulary ----------

export function meldName(locale: Locale, kind: GroupKind): string {
  return template(locale, `meld.${kind}`);
}

export function claimLabel(locale: Locale, kind: ClaimLabelKind): string {
  return template(locale, `claim.${kind}`);
}

export function seatLabel(locale: Locale, wind: SeatWind): string {
  return template(locale, `seat.${wind}`);
}

export function phaseLabel(locale: Locale, phase: string): string {
  return hasString(locale, `phase.${phase}`) ? template(locale, `phase.${phase}`) : phase;
}

/** A short, human message for an engine or protocol error code. */
export function errorMessage(locale: Locale, code: string, fallback = ""): string {
  return hasString(locale, `error.${code}`) ? template(locale, `error.${code}`) : fallback || code;
}

/** Fallback seat name when a mode does not supply its own. */
export function defaultPlayerName(locale: Locale, playerId: PlayerId): string {
  return translate(locale, "player.generic", { number: playerId + 1 });
}

// ---------- longer copy ----------

export function glossary(locale: Locale): readonly GlossaryEntry[] {
  return dictionary(locale).glossary;
}

export function isLessonId(id: string): id is LessonId {
  return (LESSON_IDS as readonly string[]).includes(id);
}

export function lessonCopy(locale: Locale, id: string): LessonCopy | null {
  return isLessonId(id) ? dictionary(locale).lessons[id] : null;
}

export function lessonTitle(locale: Locale, id: string): string {
  return lessonCopy(locale, id)?.title ?? id;
}
